import { ButtonProps, buttonClasses } from '@mui/material/Button';
import { touchRippleClasses } from '@mui/material/ButtonBase';
import { alpha, Theme } from '@mui/material/styles';

import { deepNavy, peachScale, redScale } from '../../palette-v2';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
  }
}

// ----------------------------------------------------------------------

// Shared disabled tokens
const DISABLED_BG = deepNavy[200];
const DISABLED_TEXT = deepNavy[500];
const DISABLED_BORDER = deepNavy[300];

// Ripple child selectors per interaction state
const ripple = `& .${touchRippleClasses.ripple} .${touchRippleClasses.child}`;
const rippleOnFocus = `&.Mui-focusVisible .${touchRippleClasses.ripple} .${touchRippleClasses.child}`;
const rippleOnActive = `&:active .${touchRippleClasses.ripple} .${touchRippleClasses.child}`;
export function button(theme: Theme) {
  const lightMode = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';
    const primaryColor = ownerState.color === 'primary';
    const secondaryColor = ownerState.color === 'secondary';
    const errorColor = ownerState.color === 'error';

    const containedVariant = ownerState.variant === 'contained';
    const outlinedVariant = ownerState.variant === 'outlined';
    const textVariant = ownerState.variant === 'text';
    const softVariant = ownerState.variant === 'soft';

    const smallSize = ownerState.size === 'small';
    const mediumSize = ownerState.size === 'medium';
    const largeSize = ownerState.size === 'large';

    const defaultStyle = {
      borderRadius: 14,
      textTransform: 'none' as const,
      fontWeight: 500,
      boxShadow: 'none',
      '&:hover': { boxShadow: 'none' },
      '&:active': { boxShadow: 'none' },
      '&.Mui-focusVisible': { boxShadow: 'none' },
      ...(inheritColor && {
        ...(containedVariant && {
          color: lightMode ? theme.palette.common.white : theme.palette.grey[800],
          backgroundColor: lightMode ? theme.palette.grey[800] : theme.palette.common.white,
          '&:hover': {
            backgroundColor: lightMode ? theme.palette.grey[700] : theme.palette.grey[400],
            boxShadow: 'none',
          },
        }),
        ...(outlinedVariant && {
          borderColor: alpha(theme.palette.grey[500], 0.32),
          backgroundColor: 'white',
          '&:hover': { backgroundColor: theme.palette.action.hover, boxShadow: 'none' },
        }),
        ...(textVariant && {
          '&:hover': { backgroundColor: theme.palette.action.hover, boxShadow: 'none' },
        }),
        ...(softVariant && {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          '&:hover': { backgroundColor: alpha(theme.palette.grey[500], 0.24), boxShadow: 'none' },
        }),
      }),
    };

    // Soft variant for info/success/warning (primary/secondary/error handled separately)
    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        ...(softVariant && {
          color: theme.palette[color][lightMode ? 'dark' : 'light'],
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          '&:hover': { backgroundColor: alpha(theme.palette[color].main, 0.32), boxShadow: 'none' },
        }),
      }),
    }));

    // ── PRIMARY — Deep Navy ──────────────────────────────────────────────
    const primaryStyle = primaryColor
      ? {
          ...(containedVariant && {
            backgroundColor: deepNavy[900],
            color: deepNavy[50],
            '&:hover': { backgroundColor: deepNavy[700], boxShadow: 'none' },
            '&:active': { backgroundColor: deepNavy[500], boxShadow: 'none' },
            '&.Mui-focusVisible': { backgroundColor: deepNavy[500], boxShadow: 'none' },
            [ripple]: { backgroundColor: deepNavy[200] },
            [`&.${buttonClasses.disabled}`]: { backgroundColor: DISABLED_BG, color: DISABLED_TEXT },
          }),
          ...(outlinedVariant && {
            borderWidth: 1.5,
            borderColor: deepNavy[900],
            color: deepNavy[900],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: deepNavy[200],
              borderColor: deepNavy[700],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: 'transparent',
              borderColor: deepNavy[500],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
              borderColor: deepNavy[500],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            [rippleOnFocus]: { backgroundColor: deepNavy[500] },
            [rippleOnActive]: { backgroundColor: deepNavy[400] },
            [`&.${buttonClasses.disabled}`]: { borderColor: DISABLED_BORDER, color: DISABLED_TEXT },
          }),
          ...(textVariant && {
            color: deepNavy[900],
            '&:hover': { backgroundColor: deepNavy[200], color: deepNavy[700], boxShadow: 'none' },
            '&:active': { backgroundColor: 'transparent', color: deepNavy[800], boxShadow: 'none' },
            '&.Mui-focusVisible': { backgroundColor: 'transparent', color: deepNavy[900], boxShadow: 'none' },
            [rippleOnFocus]: { backgroundColor: deepNavy[200] },
            [rippleOnActive]: { backgroundColor: deepNavy[300] },
            [`&.${buttonClasses.disabled}`]: { color: DISABLED_TEXT },
          }),
        }
      : {};

    // ── SECONDARY — Peach ────────────────────────────────────────────────
    const secondaryStyle = secondaryColor
      ? {
          ...(containedVariant && {
            backgroundColor: peachScale[200],
            color: deepNavy[900],
            '&:hover': { backgroundColor: peachScale[400], boxShadow: 'none' },
            '&:active': { backgroundColor: peachScale[500], color: deepNavy[50], boxShadow: 'none' },
            '&.Mui-focusVisible': { backgroundColor: peachScale[500], color: deepNavy[900], boxShadow: 'none' },
            [ripple]: { backgroundColor: alpha(deepNavy[50], 0.3) },
            [`&.${buttonClasses.disabled}`]: { backgroundColor: DISABLED_BG, color: DISABLED_TEXT },
          }),
          ...(outlinedVariant && {
            borderWidth: 1.5,
            borderColor: peachScale[400],
            color: deepNavy[900],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: peachScale[100],
              borderColor: peachScale[600],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: 'transparent',
              borderColor: peachScale[600],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
              borderColor: peachScale[600],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            [rippleOnFocus]: { backgroundColor: peachScale[100] },
            [rippleOnActive]: { backgroundColor: peachScale[200] },
            [`&.${buttonClasses.disabled}`]: { borderWidth: 1.5, borderColor: DISABLED_TEXT, color: DISABLED_TEXT },
          }),
          ...(textVariant && {
            color: peachScale[600],
            '&:hover': { backgroundColor: peachScale[50], color: peachScale[600], boxShadow: 'none' },
            '&:active': { backgroundColor: 'transparent', color: peachScale[600], boxShadow: 'none' },
            '&.Mui-focusVisible': { backgroundColor: 'transparent', color: peachScale[600], boxShadow: 'none' },
            [rippleOnFocus]: { backgroundColor: peachScale[100] },
            [rippleOnActive]: { backgroundColor: peachScale[200] },
            [`&.${buttonClasses.disabled}`]: { color: DISABLED_TEXT },
          }),
        }
      : {};

    // ── ERROR — Red ──────────────────────────────────────────────────────
    const errorStyle = errorColor
      ? {
          ...(containedVariant && {
            backgroundColor: redScale[400],
            color: deepNavy[50],
            '&:hover': { backgroundColor: redScale[500], boxShadow: 'none' },
            '&:active': { backgroundColor: redScale[600], boxShadow: 'none' },
            '&.Mui-focusVisible': { backgroundColor: redScale[600], boxShadow: 'none' },
            [ripple]: { backgroundColor: alpha(deepNavy[50], 0.3) },
            [`&.${buttonClasses.disabled}`]: { backgroundColor: DISABLED_BG, color: DISABLED_TEXT },
          }),
          ...(outlinedVariant && {
            borderWidth: 1.5,
            borderColor: redScale[400],
            color: redScale[600],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: redScale[50],
              borderColor: redScale[500],
              borderWidth: 1.5,
              color: redScale[700],
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: 'transparent',
              borderColor: redScale[600],
              borderWidth: 1.5,
              color: redScale[800],
              boxShadow: 'none',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
              borderColor: redScale[600],
              borderWidth: 1.5,
              boxShadow: 'none',
            },
            [rippleOnFocus]: { backgroundColor: redScale[300] },
            [rippleOnActive]: { backgroundColor: redScale[50] },
            [`&.${buttonClasses.disabled}`]: { borderWidth: 1.5, borderColor: DISABLED_BORDER, color: DISABLED_TEXT },
          }),
          ...(textVariant && {
            color: redScale[700],
            '&:hover': { backgroundColor: redScale[50], color: redScale[700], boxShadow: 'none' },
            '&:active': { backgroundColor: 'transparent', color: redScale[800], boxShadow: 'none' },
            '&.Mui-focusVisible': { backgroundColor: 'transparent', color: redScale[700], boxShadow: 'none' },
            [rippleOnFocus]: { backgroundColor: redScale[300] },
            [rippleOnActive]: { backgroundColor: redScale[50] },
            [`&.${buttonClasses.disabled}`]: { color: DISABLED_TEXT },
          }),
        }
      : {};

    const disabledState = {
      [`&.${buttonClasses.disabled}`]: {
        ...(softVariant && { backgroundColor: theme.palette.action.disabledBackground }),
      },
    };

    const size = {
      ...(smallSize && { height: 32, fontSize: 13, paddingLeft: 8, paddingRight: 8 }),
      ...(mediumSize && { height: 38, paddingLeft: 12, paddingRight: 12 }),
      ...(largeSize && { height: 46, fontSize: 16, paddingLeft: 24, paddingRight: 24 }),
    };

    return [defaultStyle, ...colorStyle, primaryStyle, secondaryStyle, errorStyle, disabledState, size];
  };

  return {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) => rootStyles(ownerState),
      },
    },
  };
}
